import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { checkRateLimit } from './securityUtils';
import { supabase } from '@/integrations/supabase/client';

// Rate limiting for OTP sends - 3 attempts per 10 minutes per phone number
const OTP_SEND_LIMIT = 3;
const OTP_SEND_WINDOW = 10 * 60 * 1000; // 10 minutes in milliseconds

// Rate limiting for OTP verification - 5 attempts per 10 minutes per phone number
const OTP_VERIFY_LIMIT = 5;
const OTP_VERIFY_WINDOW = 10 * 60 * 1000; // 10 minutes in milliseconds

export const sendOTP = async (phoneNumber: string): Promise<boolean> => {
  try {
    // Format phone number to ensure it has country code
    const formattedPhone = formatPhoneNumber(phoneNumber);
    
    // Check rate limit
    if (!checkRateLimit(`otp_send:${formattedPhone}`, OTP_SEND_LIMIT, OTP_SEND_WINDOW)) {
      toast.error('Too many verification attempts. Please try again later.');
      return false;
    }
    
    // Invoke the Edge Function
    const response = await supabase.functions.invoke('twilio-verify', {
      body: {
        action: 'send',
        phoneNumber: formattedPhone,
      },
    });

    if (response.error) {
      console.error('Error sending OTP:', response.error);
      toast.error('Failed to send verification code. Please try again.');
      return false;
    }

    // Check for fallback mode
    if (response.data?.message?.includes('demo mode')) {
      console.log(`[DEMO] OTP sent to ${formattedPhone}. Check function logs for the code.`);
      toast.success('Verification code sent to your phone (DEMO: code is 123456)');
    } else {
      toast.success('Verification code sent to your phone');
    }
    
    return true;
  } catch (error) {
    console.error('Error sending OTP:', error);
    toast.error('Failed to send verification code. Please try again.');
    return false;
  }
};

export const verifyOTP = async (phoneNumber: string, otpToVerify: string): Promise<boolean> => {
  try {
    // Format phone number to ensure it has country code
    const formattedPhone = formatPhoneNumber(phoneNumber);
    
    // Check rate limit
    if (!checkRateLimit(`otp_verify:${formattedPhone}`, OTP_VERIFY_LIMIT, OTP_VERIFY_WINDOW)) {
      toast.error('Too many verification attempts. Please try again later.');
      return false;
    }
    
    const response = await supabase.functions.invoke('twilio-verify', {
      body: {
        action: 'verify',
        phoneNumber: formattedPhone,
        otp: otpToVerify,
      },
    });

    if (response.error) {
      console.error('Error verifying OTP:', response.error);
      toast.error(response.error.message || 'Verification failed. Please try again.');
      return false;
    }

    return response.data?.success || false;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    toast.error('Verification failed. Please try again.');
    return false;
  }
};

// Helper function to ensure phone number has country code
export const formatPhoneNumber = (phone: string): string => {
  // If phone doesn't start with +, assume it's a US number
  if (!phone.startsWith('+')) {
    // Remove any non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If it doesn't have country code (assuming US), add +1
    if (digits.length <= 10) {
      return `+1${digits}`;
    } else {
      return `+${digits}`;
    }
  }
  return phone;
};

// Helper function to format phone for display
export const formatPhoneForDisplay = (phone: string): string => {
  const formattedPhone = formatPhoneNumber(phone);
  
  // Simple display formatting
  if (formattedPhone.startsWith('+1') && formattedPhone.length === 12) {
    const digits = formattedPhone.substring(2);
    return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
  
  // For non-US numbers, just return the formatted phone
  return formattedPhone;
};

// For development/testing, provide a way to bypass OTP verification
// Remove this in production!
export const bypassOTP = (otpToVerify: string): boolean => {
  return otpToVerify === '123456';
};
